import { apiService } from './apiService';
import type {
  BillingOverview,
  SubscriptionPlan,
  Subscription,
  PaymentMethod,
  Invoice,
  CreateSubscriptionRequest,
  AddPaymentMethodRequest,
  SetupIntent,
  PaginatedResult,
  SubscriptionBenefits,
  BillingConfig,
} from '../../types/billing';

export async function getBillingConfig(): Promise<BillingConfig | null> {
  const response = await apiService.get<BillingConfig>('/api/billing/config');
  if (response.error) {
    console.error('Failed to get billing config:', response.error);
    return null;
  }
  return response.data || null;
}

export async function getBillingOverview(): Promise<BillingOverview | null> {
  const response = await apiService.get<BillingOverview>('/api/billing/overview');
  if (response.error) {
    console.error('Failed to get billing overview:', response.error);
    return null;
  }
  return response.data || null;
}

export async function getAvailablePlans(): Promise<SubscriptionPlan[]> {
  const response = await apiService.get<SubscriptionPlan[]>('/api/billing/plans');
  return response.data || [];
}

export async function getCurrentSubscription(): Promise<Subscription | null> {
  const response = await apiService.get<{ subscription?: Subscription }>('/api/billing/subscription');
  return response.data?.subscription || response.data as Subscription || null;
}

export async function createSubscription(request: CreateSubscriptionRequest): Promise<Subscription> {
  const response = await apiService.post<Subscription>('/api/billing/subscription', request);
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function changePlan(newPlanId: number): Promise<Subscription> {
  const response = await apiService.put<Subscription>('/api/billing/subscription/plan', { newPlanId });
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function cancelSubscription(immediately: boolean = false, reason?: string): Promise<Subscription> {
  const response = await apiService.post<Subscription>('/api/billing/subscription/cancel', { immediately, reason });
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function reactivateSubscription(): Promise<Subscription> {
  const response = await apiService.post<Subscription>('/api/billing/subscription/reactivate');
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await apiService.get<PaymentMethod[]>('/api/billing/payment-methods');
  return response.data || [];
}

export async function addPaymentMethod(paymentMethodId: string, setAsDefault: boolean = true): Promise<PaymentMethod> {
  const request: AddPaymentMethodRequest = { paymentMethodId, setAsDefault };
  const response = await apiService.post<PaymentMethod>('/api/billing/payment-methods', request);
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function removePaymentMethod(id: number): Promise<boolean> {
  const response = await apiService.delete(`/api/billing/payment-methods/${id}`);
  return !response.error;
}

export async function setDefaultPaymentMethod(id: number): Promise<boolean> {
  const response = await apiService.put(`/api/billing/payment-methods/${id}/default`, {});
  return !response.error;
}

export async function getInvoices(page: number = 1, pageSize: number = 10): Promise<PaginatedResult<Invoice>> {
  const response = await apiService.get<PaginatedResult<Invoice>>(
    `/api/billing/invoices?page=${page}&pageSize=${pageSize}`
  );
  return response.data || { items: [], totalCount: 0, page, pageSize, totalPages: 0, hasNextPage: false, hasPreviousPage: false };
}

export async function getSetupIntent(): Promise<SetupIntent> {
  const response = await apiService.get<SetupIntent>('/api/billing/setup-intent');
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function getBenefits(): Promise<SubscriptionBenefits> {
  const response = await apiService.get<SubscriptionBenefits>('/api/billing/benefits');
  if (response.error) {
    // Return free tier benefits as default
    return {
      planCode: 'free',
      maxRoomCapacity: 4,
      dailyCurrencyBonus: 0,
      adFree: false,
      hasPremiumItems: false,
      priorityMatchmaking: false,
      characterSlots: 1,
    };
  }
  return response.data!;
}

export const billingService = {
  getBillingConfig,
  getBillingOverview,
  getAvailablePlans,
  getCurrentSubscription,
  createSubscription,
  changePlan,
  cancelSubscription,
  reactivateSubscription,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
  getInvoices,
  getSetupIntent,
  getBenefits,
};
